/**
 * MANIFEST DE ASSETS (fuente única de verdad del arte).
 *
 * Cada entrada asocia una CLAVE VISUAL estable (p. ej. "machinery.mixer")
 * con el fichero de imagen que debe renderizarse y con el nombre del
 * PLACEHOLDER vectorial que se usa mientras el arte no exista.
 *
 * REGLA DE ORO: la lógica del juego NUNCA importa de aquí. Solo la capa
 * visual (SpriteFactory) lo consulta. Cambiar "mixer_v1.webp" por
 * "mixer_final.webp" se hace SOLO en este fichero; misión, riesgo,
 * interacción, guardado y posición no se tocan (criterio de éxito Fase 29).
 */

/** Un asset del juego: dónde está su imagen y cómo se ancla/escala. */
export interface AssetDef {
  /** Ruta servida por Vite (bajo /public). Puede no existir todavía. */
  path: string;
  /**
   * Nombre del placeholder vectorial (función de art/props.ts) que se dibuja
   * si el PNG/WebP aún no está disponible. Así el juego nunca se rompe.
   */
  fallback: string;
  /** Origen del sprite (0..1). Por defecto los PIES/base (0.5, 1) para el depth. */
  originX?: number;
  originY?: number;
  /** Escala de dibujo del sprite real. */
  scale?: number;
  /**
   * ¿Existe ya el archivo real? Solo se PRECARGAN los que están a true, así
   * activar LOAD_REAL_ASSETS no genera 404 de los que aún no se han producido.
   */
  available?: boolean;
}

/** Clave visual estable → definición del asset. */
export type VisualKey = string;

/**
 * Catálogo por categorías (Fase 21). Las rutas apuntan a dónde IRÁN los
 * assets; hasta que existan, se usa el `fallback` vectorial.
 */
export const ConstructionAssets: Record<VisualKey, AssetDef> = {
  // --- personaje jugable (EPI) ---
  "player.epi": { path: "assets/characters/epi/epi_idle.webp", fallback: "epiPlayer", originX: 0.5, originY: 1 },

  // --- trabajadores (NPC) ---
  "worker.antonio": { path: "assets/characters/workers/antonio.webp", fallback: "workerAntonio", originX: 0.5, originY: 1 },
  "worker.lucia": { path: "assets/characters/workers/lucia.webp", fallback: "workerLucia", originX: 0.5, originY: 1 },
  "worker.manolo": { path: "assets/characters/workers/manolo.webp", fallback: "workerManolo", originX: 0.5, originY: 1 },

  // --- seguridad / EPIs ---
  "safety.helmet": { path: "assets/construction/helmet_01.webp", fallback: "helmet", originX: 0.5, originY: 1 },
  "safety.locker": { path: "assets/construction/ppe_locker_01.webp", fallback: "locker", originX: 0.5, originY: 1 },
  "safety.cone": { path: "assets/construction/safety_cone_01.webp", fallback: "cone", originX: 0.5, originY: 1 },
  "safety.fence": { path: "assets/construction/safety_barrier_01.webp", fallback: "fence", originX: 0.5, originY: 1 },
  "safety.warning": { path: "assets/construction/warning_sign_01.webp", fallback: "warningSign", originX: 0.5, originY: 1 },
  "safety.extinguisher": { path: "assets/construction/extinguisher_01.webp", fallback: "extinguisher", originX: 0.5, originY: 1 },

  // --- maquinaria ---
  "machinery.mixer": { path: "assets/construction/cement_mixer_01.webp", fallback: "mixer", originX: 0.5, originY: 1 },
  "machinery.wheelbarrow": { path: "assets/construction/wheelbarrow_01.webp", fallback: "wheelbarrow", originX: 0.5, originY: 1 },

  // --- materiales ---
  "material.sacks": { path: "assets/construction/cement_bags_01.webp", fallback: "sacks", originX: 0.5, originY: 1 },
  "material.pipes": { path: "assets/construction/pipes_01.webp", fallback: "pipes", originX: 0.5, originY: 1 },
  "material.pallet": { path: "assets/construction/wood_pallet_01.webp", fallback: "pallet", originX: 0.5, originY: 1 },
  "material.crate": { path: "assets/construction/crate_01.webp", fallback: "crate", originX: 0.5, originY: 1 },
  "material.barrel": { path: "assets/construction/barrel_01.webp", fallback: "barrel", originX: 0.5, originY: 1 },

  // --- arquitectura de obra ---
  "arch.scaffold": { path: "assets/construction/scaffold_01.webp", fallback: "scaffold", originX: 0.5, originY: 1 },
  "arch.wall": { path: "assets/construction/construction_wall_01.webp", fallback: "wall", originX: 0.5, originY: 1 },
  "arch.platform": { path: "assets/construction/concrete_platform_01.webp", fallback: "platform", originX: 0.5, originY: 1 },
  "arch.ladder": { path: "assets/construction/ladder_01.webp", fallback: "ladder", originX: 0.5, originY: 1 },
  "arch.hut": { path: "assets/construction/site_hut_01.webp", fallback: "hut", originX: 0.5, originY: 1 },

  // --- herramienta caída (objeto de gameplay) ---
  "tool.fallen": { path: "assets/construction/fallen_tool_01.webp", fallback: "fallenTool", originX: 0.5, originY: 1 },
};

/** Todas las claves conocidas (para precarga). */
export function allAssetKeys(): VisualKey[] {
  return Object.keys(ConstructionAssets);
}

// ===================== PERSONAJES (spritesheets) =====================

/** Direcciones isométricas. Orden = filas del spritesheet (arriba->abajo). */
export const DIRECTIONS = ["NE", "NW", "SE", "SW"] as const;
export type Direction = (typeof DIRECTIONS)[number];

/** Estados de animación (ampliable: inspect/pickup/talk/use/equip/celebrate/hurt). */
export type CharState = "idle" | "walk";

/** Un spritesheet de un estado: filas = direcciones, columnas = frames. */
export interface SheetDef {
  path: string;
  frameWidth: number;
  frameHeight: number;
  /** Frames por dirección (columnas). */
  framesPerDir: number;
  frameRate: number;
  /** ¿Existe ya el archivo? Solo se precargan los disponibles. */
  available?: boolean;
}

/** Definición de un personaje: sus spritesheets + su placeholder vectorial. */
export interface CharacterDef {
  idle: SheetDef;
  walk: SheetDef;
  /** Placeholder vectorial si no hay sheet: "epi" o preset de worker. */
  fallback: string;
}

/**
 * Catálogo de personajes. EPI es la REFERENCIA DE ESCALA (~1,75 m ≈ 128×192).
 * El resto se dibuja coherente con él. Rutas = dónde IRÁN los sheets.
 */
export const Characters: Record<string, CharacterDef> = {
  epi: {
    // idle real: turnaround 2D (generado) empaquetado a 1 frame por dirección (NE/NW/SE/SW)
    idle: { path: "assets/characters/epi/epi_idle.png", frameWidth: 128, frameHeight: 192, framesPerDir: 1, frameRate: 1, available: true },
    walk: { path: "assets/characters/epi/epi_walk.webp", frameWidth: 128, frameHeight: 192, framesPerDir: 8, frameRate: 12 },
    fallback: "epi",
  },
  antonio: {
    idle: { path: "assets/characters/workers/worker_antonio_idle.png", frameWidth: 128, frameHeight: 192, framesPerDir: 1, frameRate: 1 },
    // Antonio de momento solo idle; reutiliza idle como walk hasta tener animación.
    walk: { path: "assets/characters/workers/worker_antonio_idle.png", frameWidth: 128, frameHeight: 192, framesPerDir: 1, frameRate: 1 },
    fallback: "workerAntonio",
  },
};

export function characterSheetKey(charKey: string, state: CharState): string {
  return `char:${charKey}:${state}`;
}
